import React from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

/**
 * PageKpis
 * -------------------------------------------------------
 * Usage:
 * <PageKpis
 *   items={[
 *     { label: "Revenue", value: 124834, format: "currency", currency: "USD", tone: "success", icon: <DollarSign />, delta: { value: 4.2, direction: "up" }, sparkline: [3,4,2,5,6,8,9] },
 *     { label: "Live Changes", value: 12, tone: "success", icon: <CheckCircle2 /> },
 *     { label: "Testing", value: 8, tone: "info", icon: <Clock /> },
 *     { label: "Needs Attention", value: 2, tone: "destructive", icon: <AlertCircle /> },
 *   ]}
 * />
 */

const tones = {
  success: { chipBg: "bg-success/10", chipText: "text-success" },
  info: { chipBg: "bg-info/10", chipText: "text-info" },
  warning: { chipBg: "bg-warning/10", chipText: "text-warning" },
  destructive: { chipBg: "bg-destructive/10", chipText: "text-destructive" },
  brand: { chipBg: "bg-brand/10", chipText: "text-brand" },
  neutral: { chipBg: "bg-muted", chipText: "text-muted-foreground" },
};

function cx(...cls) {
  return cls.filter(Boolean).join(" ");
}

function formatValue(
  value,
  { format, currency = "USD", compact = false, maximumFractionDigits } = {}
) {
  if (format === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: compact ? "compact" : "standard",
      maximumFractionDigits: maximumFractionDigits ?? (compact ? 1 : 0),
      minimumFractionDigits: 0,
    }).format(Number(value || 0));
  }
  // SOUND-661: byte counts. A caller pre-formatting to "2298.7 MB" and passing
  // the STRING renders NaN, because every branch here ends in Number(value) —
  // which is exactly what happened on /files. Formatting belongs in here.
  if (format === "bytes") {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i += 1;
    }
    // Whole bytes read oddly as "512.0 B"; everything above that wants a decimal.
    return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
  }
  // SOUND-837: a caller-formatted display value ("Never", "—", "3.2"), rendered
  // verbatim. Opt-in only — every other format still goes through Number().
  if (format === "text") return value ?? "—";
  if (format === "percent") {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: maximumFractionDigits ?? 1,
      minimumFractionDigits: 0,
    }).format(Number(value || 0));
  }
  // default number
  return new Intl.NumberFormat("en-US", {
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: maximumFractionDigits ?? 0,
  }).format(Number(value || 0));
}

function Delta({ delta }) {
  if (!delta || delta.value == null) return null;
  const dir =
    delta.direction ||
    (delta.value > 0 ? "up" : delta.value < 0 ? "down" : "neutral");
  const isUp = dir === "up";
  const isDown = dir === "down";
  const Icon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : Minus;
  const color = isUp
    ? "text-success"
    : isDown
    ? "text-destructive"
    : "text-muted-foreground";
  const sign = isUp ? "+" : isDown ? "" : "";
  const val =
    delta.unit === "%"
      ? `${sign}${formatValue(Math.abs(delta.value) / 100, {
          format: "percent",
          maximumFractionDigits: 1,
        })}`
      : `${sign}${formatValue(Math.abs(delta.value), {
          compact: true,
          maximumFractionDigits: 1,
        })}`;

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 text-xs font-medium",
        color
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {val}
    </span>
  );
}

function Sparkline({ data = [], className }) {
  if (!data || data.length < 2) return null;
  const w = 84,
    h = 28,
    pad = 2;
  const min = Math.min(...data),
    max = Math.max(...data);
  const span = max - min || 1;
  const points = data.map((d, i) => {
    const x = pad + (i * (w - pad * 2)) / (data.length - 1);
    const y = h - pad - ((d - min) * (h - pad * 2)) / span;
    return [x, y];
  });
  const d = points
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(" ");

  // Area under curve
  const area = `${d} L ${points[points.length - 1][0]} ${h - pad} L ${
    points[0][0]
  } ${h - pad} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={cx("block", className)}
      aria-hidden="true"
    >
      <path d={area} fill="currentColor" opacity="0.1" />
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth=".3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KpiItem({
  label,
  value,
  format,
  currency,
  compactValue,
  unit,
  icon,
  tone = "neutral",
  hint,
  delta,
  sparkline,
  loading,
  onClick,
}) {
  const toneCfg = tones[tone] || tones.neutral;
  const clickable = typeof onClick === "function";

  return (
    <div
      role={clickable ? "button" : "group"}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) =>
        clickable && (e.key === "Enter" || e.key === " ") && onClick(e)
      }
      className={cx(
        "surface p-4 rounded-lg shadow-sm",
        "transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70",
        clickable && "cursor-pointer"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          {loading ? (
            <div className="mt-2 h-6 w-28 rounded bg-muted animate-pulse" />
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <p className="text-xl font-semibold text-foreground">
                {formatValue(value, {
                  format,
                  currency,
                  compact: compactValue,
                })}
                {unit && <span className="ml-1 text-sm font-medium text-muted-foreground">{unit}</span>}
              </p>
              <Delta delta={delta} />
            </div>
          )}
          {/* A qualifier on the number itself — e.g. a total that cannot
              include every record. Rendered under the value so it reads as
              part of the figure rather than as a second metric. */}
          {!loading && hint && (
            <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{hint}</p>
          )}
        </div>

        <div
          className={cx(
            "h-9 w-9 shrink-0 grid place-items-center rounded-md",
            toneCfg.chipBg,
            toneCfg.chipText
          )}
        >
          {icon &&
            React.cloneElement(icon, {
              className: cx("h-5 w-5", icon.props?.className),
            })}
        </div>
      </div>

      {sparkline && sparkline.length > 1 && (
        <div
          className={cx(
            "mt-3",
            tones[tone]?.chipText || "text-muted-foreground"
          )}
        >
          <Sparkline data={sparkline} />
        </div>
      )}
    </div>
  );
}

export default function PageKpis({
  items = [],
  columns = { base: 1, md: 2, lg: 4 }, // responsive defaults
  className,
}) {
  // translate to Tailwind grid classes
  const colsClass = cx(
    "grid gap-4",
    `grid-cols-${columns.base || 1}`,
    columns.md && `md:grid-cols-${columns.md}`,
    columns.lg && `lg:grid-cols-${columns.lg}`
  );

  return (
    <section className={cx(colsClass, className)}>
      {items.map((item, idx) => (
        <KpiItem key={item.key || item.label || idx} {...item} />
      ))}
    </section>
  );
}
